import { useQuery } from 'react-query'
import { useAtomValue } from 'jotai'
import { Coin } from '@cosmjs/stargate'
import { stride } from 'stridejs'
import { strideAccountAtom } from '@/atoms/Wallet'
import { queryKeys } from '@/query-keys'
import { STRIDE_CHAIN_INFO, AIRDROP_IDENTIFIERS } from '@/config'
import BigNumber from 'bignumber.js'
import { fatal } from '@/utils'

interface TotalClaimableData {
  coins: Coin[]
  total: number
}

const useTotalClaimableQuery = () => {
  const strideAccount = useAtomValue(strideAccountAtom)

  const handleRequest = async (): Promise<TotalClaimableData> => {
    if (!strideAccount) {
      throw fatal('Unable to fetch total claimable while wallet is disconnected.')
    }

    const client = await stride.ClientFactory.createRPCQueryClient({
      rpcEndpoint: STRIDE_CHAIN_INFO.rpc
    })

    const data = await Promise.all(
      AIRDROP_IDENTIFIERS.map((identifier) => {
        return client.stride.claim.totalClaimable({
          address: strideAccount.key.bech32Address,
          airdropIdentifier: identifier,
          includeClaimed: true
        })
      })
    )

    const coins = data
      .filter((claim) => {
        return Boolean(claim.coins.length)
      })
      .reduce((coins, claim) => {
        return [...coins, ...claim.coins]
      }, [] as Coin[])

    const total = coins
      .reduce((total, coin) => {
        return total.plus(coin.amount)
      }, new BigNumber(0))
      .toNumber()

    return { coins, total }
  }

  return useQuery(
    queryKeys.airdropTotalClaimableAddress({ address: strideAccount?.key.bech32Address ?? '' }),
    handleRequest,
    { enabled: Boolean(strideAccount) }
  )
}

export { useTotalClaimableQuery }
